import React, { type ReactNode } from "react";

import { Flex, HelpIcon, TEXT_VARIANT_BOLD, Text } from "mailup-web-components";

import deepGet from "Helpers/deepGet";
import getIntl from "Helpers/intl";
import {
  type ColumnName,
  type CriterionAnagraphicField,
  type CriterionFilter,
  type CriterionGroups,
  type CriterionOptinDate,
  type CriterionOptoutDate,
  type CriterionOptoutMessage,
  type CriterionOptoutType,
  type CriterionPendingDate,
  type DynamicField,
  type PaginationState,
  type SearchFiltersState,
  type SearchResult,
  isCriterionAnagraphicField,
  isCriterionFilter,
  isCriterionGroups,
  isCriterionOptinDate,
  isCriterionOptoutDate,
  isCriterionOptoutMessage,
  isCriterionOptoutType,
  isCriterionPendingDate,
  isTextMatchOperatorMultipleValue,
} from "Pages/RecipientsList/model";

import {
  beRecipientApiCustomSearchForRecipients,
  beRecipientApiHasRecipients,
} from "Api/beRecipientApi";

import type {
  ContactEnum,
  CustomRecipientOut,
  CustomSearchRecipientsIn,
  DateSubscriptionEnum,
  EmailTrackingEnum,
  MiscellaneousEnum,
  StatusEnum,
  SubscriptionStatus,
  SubscriptionStatusIn,
} from "Types/beRecipientsApi";

import {
  STORE_KEY_ID_LIST,
  STORE_KEY_LIST,
  evalCondition,
  fromUTCtoZoneFormatted,
  handleFrom,
  handleTo,
  storeGet,
} from "mailup-react-helpers";

import { getOptoutText } from "Pages/RecipientsList/recoil/services/recipient";

export const toDateUI = (
  strDate: string | undefined,
  dateFormat = "YYYY-MM-DD",
): string | undefined => {
  if (typeof strDate === "undefined" || strDate === null) {
    return undefined;
  }

  const { TimeZoneCode } = storeGet(STORE_KEY_LIST);

  return fromUTCtoZoneFormatted(strDate, TimeZoneCode, dateFormat);
};

export const toSearchResult = (resultIn: CustomRecipientOut): SearchResult => {
  const canEdit = evalCondition("uo:EDIT") as boolean;

  const email =
    typeof resultIn.Email === "string" && resultIn.Email.length > 0
      ? resultIn.Email
      : "";

  const tmpMobile = `${
    typeof resultIn.MobilePrefix === "string" ? resultIn.MobilePrefix : ""
  } ${typeof resultIn.MobileNumber === "string" ? resultIn.MobileNumber : ""}`;

  const mobile = tmpMobile.length > 1 ? tmpMobile : "";

  const EmailSubscriptionStatus =
    resultIn.EmailSubscription?.Status === ""
      ? undefined
      : resultIn.EmailSubscription?.Status;

  const SMSSubscriptionStatus =
    resultIn.SmsSubscription?.Status === ""
      ? undefined
      : resultIn.SmsSubscription?.Status;

  return {
    Id: resultIn.Id,
    Email: email,
    Mobile: mobile,
    MobilePrefix: resultIn.MobilePrefix ?? "",
    MobileNumber: resultIn.MobileNumber ?? "",
    Fields: resultIn.Fields.map(({ Id, Value }) => ({
      id: Number.parseInt(Id, 10),
      value: Value,
    })),
    RegistrationDate: toDateUI(resultIn.RegistrationDate),
    EmailSubscriptionStatus,
    EmailSubscriptionDate: toDateUI(
      resultIn.EmailSubscription?.SubscriptionDate,
    ),
    EmailPendingDate: toDateUI(resultIn.EmailSubscription?.PendingDate),
    EmailUnsubscriptionDate: toDateUI(
      resultIn.EmailSubscription?.UnsubscriptionDate,
    ),
    ConfirmedOptin: resultIn.EmailSubscription?.ConfirmedOptin ?? undefined,
    ReasonEmail: resultIn.EmailSubscription?.ReasonEmail ?? undefined,
    SMSSubscriptionStatus,
    SMSSubscriptionDate: toDateUI(resultIn.SmsSubscription?.SubscriptionDate),
    SMSUnsubscriptionDate: toDateUI(
      resultIn.SmsSubscription?.UnsubscriptionDate,
    ),
    ReasonSms: resultIn.SmsSubscription?.ReasonSms ?? undefined,
    TrackingState: resultIn.EmailTracking?.Enabled,
    TrackingLastChange: toDateUI(
      resultIn.EmailTracking?.LastChange,
      "YYYY-MM-DD HH:mm",
    ),

    actions: {
      profile: true,
      edit: canEdit,
      unsubscribe: canEdit,
      delete: canEdit,
      sendConfirmationEmail: false,
      canChangeEmailStatus: false,
      canChangeSmsStatus: false,
    },
  };
};

export const toTextSpec = (c: CriterionAnagraphicField): any => {
  const values = isTextMatchOperatorMultipleValue(c.matchOperator)
    ? c.value
        .split(",")
        .map((entry) => entry.trim())
        .filter((s) => s.length > 0)
    : [c.value.trim()];

  return {
    SpecType: "TextSpec",
    Spec: {
      Identifier: c.identifier,
      TextMatchOperator: c.matchOperator,
      Values: values,
    },
  };
};

export const toGroupSpec = (c: CriterionGroups): any => {
  return {
    SpecType: "GroupSpec",
    Spec: {
      GroupMatchOperator: c.matchOperator,
      Values:
        c.matchOperator === "NotBelongsTo"
          ? c.values.filter((g) => g !== 0)
          : c.values,
    },
  };
};

export const toSubscriptionOptinSpec = (c: CriterionOptinDate): any => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST);

  return {
    SpecType: "SubscriptionSpec",
    Spec: {
      SubscriptionMatchOperator:
        c.channel === "email" ? "EmailSubscribed" : "SmsSubscribed",
      Dates: {
        Type: "DateRangeDetails",
        Details: {
          From:
            (c.datesType === "afterDate" || c.datesType === "interval") &&
            c.dateFrom !== undefined
              ? handleFrom(c.dateFrom, TimeZoneCode)
              : undefined,
          To:
            (c.datesType === "beforeDate" || c.datesType === "interval") &&
            c.dateTo !== undefined
              ? handleTo(c.dateTo, TimeZoneCode)
              : undefined,
        },
      },
    },
  };
};

export const toSubscriptionOptoutSpec = (c: CriterionOptoutDate): any => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST);

  return {
    SpecType: "SubscriptionSpec",
    Spec: {
      SubscriptionMatchOperator:
        c.channel === "email" ? "EmailUnsubscribed" : "SmsUnsubscribed",
      Dates: {
        Type: "DateRangeDetails",
        Details: {
          From:
            (c.datesType === "afterDate" || c.datesType === "interval") &&
            c.dateFrom !== undefined
              ? handleFrom(c.dateFrom, TimeZoneCode)
              : undefined,
          To:
            (c.datesType === "beforeDate" || c.datesType === "interval") &&
            c.dateTo !== undefined
              ? handleTo(c.dateTo, TimeZoneCode)
              : undefined,
        },
      },
    },
  };
};

export const toSubscriptionPendingSpec = (c: CriterionPendingDate): any => {
  const { TimeZoneCode } = storeGet(STORE_KEY_LIST);

  return {
    SpecType: "SubscriptionSpec",
    Spec: {
      SubscriptionMatchOperator: "EmailPending",
      Dates: {
        Type: "DateRangeDetails",
        Details: {
          From:
            (c.datesType === "afterDate" || c.datesType === "interval") &&
            c.dateFrom !== undefined
              ? handleFrom(c.dateFrom, TimeZoneCode)
              : undefined,
          To:
            (c.datesType === "beforeDate" || c.datesType === "interval") &&
            c.dateTo !== undefined
              ? handleTo(c.dateTo, TimeZoneCode)
              : undefined,
        },
      },
    },
  };
};

export const toSubscriptionOptoutTypeSpec = (c: CriterionOptoutType): any => {
  return {
    SpecType: "SubscriptionSpec",
    Spec: {
      SubscriptionMatchOperator:
        c.channel === "email" ? "EmailUnsubscribed" : "SmsUnsubscribed",
      OptOutType: c.optoutType,
    },
  };
};

export const toSubscriptionOptoutMessageSpec = (
  c: CriterionOptoutMessage,
): any => {
  return {
    SpecType: "SubscriptionSpec",
    Spec: {
      SubscriptionMatchOperator: "EmailUnsubscribed",
      OptOutMessageId: c.idMessage,
    },
  };
};

export const toSubscriptionFilterSpec = (c: CriterionFilter): any => {
  return {
    SpecType: "FilterSpec",
    Spec: {
      EvalMode: true,
      ValuesEvalMode: c.mode,
      Values: [...c.values],
    },
  };
};

export const toContactFieldCondition = (contact: string): any => {
  return contact.trim().length > 0
    ? [
        {
          SpecType: "TextSpec",
          Spec: {
            Identifier: "Contacts",
            TextMatchOperator: "TextContains",
            Values: [contact.trim()],
          },
        },
      ]
    : [];
};

export const searchFiltersToConditions = (
  filters: SearchFiltersState,
): any[] => {
  const contactConditions = toContactFieldCondition(filters.contact.value);

  const anagraphicFieldConditions = filters.criteria.value
    .filter(isCriterionAnagraphicField)
    .map(toTextSpec);

  const groupsConditions = filters.criteria.value
    .filter(isCriterionGroups)
    .map(toGroupSpec);

  const optinDateConditions = filters.criteria.value
    .filter(isCriterionOptinDate)
    .map(toSubscriptionOptinSpec);

  const pendingDateConditions = filters.criteria.value
    .filter(isCriterionPendingDate)
    .map(toSubscriptionPendingSpec);

  const optoutDateConditions = filters.criteria.value
    .filter(isCriterionOptoutDate)
    .map(toSubscriptionOptoutSpec);

  const optoutTypeConditions = filters.criteria.value
    .filter(isCriterionOptoutType)
    .map(toSubscriptionOptoutTypeSpec);

  const optoutMessageConditions = filters.criteria.value
    .filter(isCriterionOptoutMessage)
    .map(toSubscriptionOptoutMessageSpec);

  const filtersConditions = filters.criteria.value
    .filter(isCriterionFilter)
    .map(toSubscriptionFilterSpec);

  return [
    ...contactConditions,
    ...anagraphicFieldConditions,
    ...groupsConditions,
    ...optinDateConditions,
    ...pendingDateConditions,
    ...optoutDateConditions,
    ...optoutTypeConditions,
    ...optoutMessageConditions,
    ...filtersConditions,
  ]
    .filter((x) => x !== undefined)
    .map((c, index) => ({
      ...c,
      JoinType: index === 0 ? "Nil" : "And",
    }));
};

export const fetchEmptyState = async (): Promise<boolean> => {
  const idList = storeGet(STORE_KEY_ID_LIST);

  const hasRecipients = (await beRecipientApiHasRecipients(idList)) as boolean;

  return !hasRecipients;
};

export const columnsToContactProperties = (
  columns: ColumnName[],
): ContactEnum[] => {
  return [
    ...(columns.includes("Email") ? ["EMAIL"] : []),
    ...(columns.includes("Mobile") ? ["MOBILEPREFIX", "MOBILENUMBER"] : []),
  ] as ContactEnum[];
};

export const columnsToFields = (columns: ColumnName[]): number[] => {
  return columns
    .filter((c) => c.startsWith("Field"))
    .map((c) => {
      const m = c.match(/Field(\d+)/);
      if (m != null) {
        return Number.parseInt(m[1], 10);
      }

      return undefined;
    })
    .filter((c) => c !== undefined) as number[];
};

export const columnsToDates = (
  columns: ColumnName[],
): DateSubscriptionEnum[] => {
  return [
    ...(columns.includes("RegistrationDate") ? ["REGISTRATIONDATE"] : []),
    ...(columns.includes("EmailPendingDate") ? ["EMAILPENDINGDATE"] : []),
    ...(columns.includes("EmailSubscriptionDate")
      ? ["EMAILSUBSCRIPTIONDATE"]
      : []),
    ...(columns.includes("EmailUnsubscriptionDate")
      ? ["EMAILUNSUBSCRIPTIONDATE"]
      : []),
    ...(columns.includes("SMSSubscriptionDate") ? ["SMSSUBSCRIPTIONDATE"] : []),
    ...(columns.includes("SMSUnsubscriptionDate")
      ? ["SMSUNSUBSCRIPTIONDATE"]
      : []),
  ] as DateSubscriptionEnum[];
};

export const columnsToMiscellaneous = (
  columns: ColumnName[],
): MiscellaneousEnum[] => {
  return [
    ...(columns.includes("ReasonEmail") ? ["REASONEMAIL"] : []),
    ...(columns.includes("ReasonSms") ? ["REASONSMS"] : []),
    ...(columns.includes("ConfirmedOptin") ? ["CONFIRMEDOPTIN"] : []),
  ] as MiscellaneousEnum[];
};

export const columnsToStatus = (columns: ColumnName[]): StatusEnum[] => {
  return [
    ...(columns.includes("EmailSubscriptionStatus") ? ["EMAIL"] : []),
    ...(columns.includes("SMSSubscriptionStatus") ? ["SMS"] : []),
  ] as StatusEnum[];
};

export const columnsToEmailTracking = (
  columns: ColumnName[],
): EmailTrackingEnum[] => {
  return [
    ...(columns.includes("TrackingState") ? ["STATE"] : []),
    ...(columns.includes("TrackingLastChange") ? ["LASTCHANGE"] : []),
  ] as EmailTrackingEnum[];
};

export const toSubscriptionStatus = (
  status: SubscriptionStatus[],
): SubscriptionStatusIn[] => {
  return status.length > 0
    ? [
        {
          Operation: "OR",
          Values: [...status],
        },
      ]
    : [];
};

export const fetchResults = async (
  filters: SearchFiltersState,
  columns: ColumnName[],
  { pageNumber, pageSize, orderBy, orderDescending }: PaginationState,
  hasForcedRecipient = false,
): Promise<{
  items: SearchResult[];
  pageNumber: number;
  pageSize: number;
  totalItems: number;
}> => {
  if (hasForcedRecipient) {
    return {
      items: [],
      pageNumber,
      pageSize,
      totalItems: 0,
    };
  }

  const idList = storeGet(STORE_KEY_ID_LIST);
  //  TODO convert filters and columns to CustomSearchRecipientsIn
  //

  const conditions = searchFiltersToConditions(filters);

  const includeContactProperties = columnsToContactProperties(columns);
  const includeFields = columnsToFields(columns);
  const includeDates = columnsToDates(columns);
  const includeStatus = columnsToStatus(columns);
  const includeMiscellaneous = columnsToMiscellaneous(columns);
  const includeEmailTracking = columnsToEmailTracking(columns);

  const searchIn: CustomSearchRecipientsIn = {
    Conditions: [...conditions],
    SubscriptionStatus: toSubscriptionStatus(filters.status.value),
    IncludeProperties: {
      Contacts: [...includeContactProperties],
      Fields: [...includeFields],
      DateSubscription: [...includeDates],
      Status: [...includeStatus],
      Miscellaneous: [...includeMiscellaneous],
      EmailTracking: [...includeEmailTracking],
    },
  };

  const { Items, PageNumber, PageSize, TotalItems } =
    await beRecipientApiCustomSearchForRecipients(
      idList,
      { pageNumber, pageSize, orderBy, orderDescending },
      searchIn,
    );

  if (Items.length === 0 && TotalItems > 0) {
    //  wrong page
    const results2 = await beRecipientApiCustomSearchForRecipients(
      idList,
      {
        pageNumber: Math.ceil(TotalItems / PageSize),
        pageSize,
        orderBy,
        orderDescending,
      },
      searchIn,
    );

    return {
      items: results2.Items.map(toSearchResult),
      pageNumber: results2.PageNumber,
      pageSize: results2.PageSize,
      totalItems: results2.TotalItems,
    };
  }

  return {
    items: Items.map(toSearchResult),
    pageNumber: PageNumber,
    pageSize: PageSize,
    totalItems: TotalItems,
  };
};

export const getYesNoText = (intl: any, value: boolean): string => {
  return value
    ? intl.formatMessage({ id: "Load.MailupCommon.Yes" })
    : intl.formatMessage({ id: "Load.MailupCommon.No" });
};

export const getRecipientFieldValue = (
  item: SearchResult,
  columnName: ColumnName,
): string | undefined => {
  if (columnName.startsWith("Field")) {
    const m = columnName.match(/Field(\d+)/);
    if (m !== null) {
      const fieldId = Number.parseInt(m[1], 10);

      const fieldEntry = item.Fields.find((f) => f.id === fieldId);

      if (fieldEntry !== undefined) {
        return fieldEntry.value;
      }
    }
  }

  if (columnName === "ReasonEmail") {
    return item.ReasonEmail !== undefined
      ? getOptoutText(getIntl(), item.ReasonEmail)
      : "";
  }

  if (columnName === "ReasonSms") {
    return item.ReasonSms !== undefined
      ? getOptoutText(getIntl(), item.ReasonSms)
      : "";
  }

  if (columnName === "ConfirmedOptin") {
    return item.ConfirmedOptin !== undefined
      ? getYesNoText(getIntl(), item.ConfirmedOptin)
      : "";
  }

  return deepGet(item, columnName);
};

export const getColumnName = (
  columnName: string,
  dynamicFields: DynamicField[],
  withTooltip = false,
): ReactNode => {
  const intl = getIntl();

  if (columnName.startsWith("Field")) {
    const m = columnName.match(/Field(\d+)/);
    if (m !== null) {
      const fieldId = Number.parseInt(m[1], 10);

      const df = dynamicFields.find((dfi) => dfi.id === fieldId);
      if (df !== undefined) {
        return `${df.id} - ${df.description}`;
      }
    }
  }

  switch (columnName) {
    case "EmailSubscriptionStatus":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmailSubscriptionStatus",
      });
    case "Email":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmail",
      });
    case "Mobile":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnMobile",
      });

    case "MobilePrefix":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnMobilePrefix",
      });

    case "MobileNumber":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnMobileNumber",
      });

    case "TrackingState":
      return intl.formatMessage({
        id: "Load.RecipientsList.TrackingStatus",
      });

    case "TrackingLastChange":
      return intl.formatMessage({
        id: "Load.RecipientsList.TrackinkgLastChange",
      });

    case "Engagement":
      return withTooltip ? (
        <Flex alignItems="center" gap="4px">
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: "Load.RecipientsList.ColumnEngagement",
            })}
          </Text>

          <HelpIcon
            data-event="click"
            data-tip
            data-for="engagement-tooltip"
            inline={false}
          />
        </Flex>
      ) : (
        intl.formatMessage({
          id: "Load.RecipientsList.ColumnEngagement",
        })
      );

    case "Groups":
      return intl.formatMessage({ id: "Load.RecipientsList.ColumnGroups" });

    case "RegistrationDate":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnRegistrationDate",
      });
    case "EmailPendingDate":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmailPendingDate",
      });
    case "EmailSubscriptionDate":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmailSubscriptionDate",
      });
    case "EmailUnsubscriptionDate":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmailUnsubscriptionDate",
      });
    case "SMSSubscriptionStatus":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnSMSSubscriptionStatus",
      });
    case "SMSSubscriptionDate":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnSMSSubscriptionDate",
      });
    case "SMSUnsubscriptionDate":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnSMSUnsubscriptionDate",
      });

    //
    case "ReasonEmail":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmailOptoutType",
      });
    case "ReasonSms":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnSMSOptoutType",
      });
    case "ConfirmedOptin":
      return intl.formatMessage({
        id: "Load.RecipientsList.ColumnEmailConfirmedOptin",
      });
  }

  return undefined;
};
